import { ClipboardList, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCampaignAdmin,
	useCreateCampaign,
	useUpdateCampaign,
} from '@/api/queries/campaigns-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { CampaignQuestionEditor } from '@/components/campaigns/campaign-question-editor'
import { AdminCardPage } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import type { CampaignQuestionDraft } from '@/types/campaign'
import { getApiErrorMessage } from '@/utils/api-error'
import { isoToDateValue } from '@/utils/campaign-dates'
import {
	createQuestionDraft,
	draftFromQuestion,
	sanitizeConditions,
	toQuestionPayload,
	validateDrafts,
} from '@/utils/campaign-draft'

/** Criação e edição de campanhas (edição: `?id=<campanha>`). Com respostas, só os dados gerais mudam. */
export const CampaignFormPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const editingId = searchParams.get('id')

	const { data: orgData } = useOrganization()
	const { data: campaign, isLoading, isError } = useCampaignAdmin(editingId)
	const createMutation = useCreateCampaign()
	const updateMutation = useUpdateCampaign()

	const [title, setTitle] = useState('')
	const [description, setDescription] = useState('')
	const [startsAt, setStartsAt] = useState('')
	const [endsAt, setEndsAt] = useState('')
	const [allowMultiple, setAllowMultiple] = useState(false)
	const [isGlobal, setIsGlobal] = useState(true)
	const [branchIds, setBranchIds] = useState<string[]>([])
	const [drafts, setDrafts] = useState<CampaignQuestionDraft[]>(() => [
		createQuestionDraft(),
	])

	useEffect(() => {
		if (!campaign) return
		setTitle(campaign.title)
		setDescription(campaign.description ?? '')
		setStartsAt(isoToDateValue(campaign.startsAt))
		setEndsAt(isoToDateValue(campaign.endsAt))
		setAllowMultiple(campaign.allowMultiple)
		setIsGlobal(campaign.isGlobal)
		setBranchIds(campaign.branchIds)
		setDrafts(campaign.questions.map(draftFromQuestion))
	}, [campaign])

	const questionsLocked = (campaign?.responseCount ?? 0) > 0
	const isSaving = createMutation.isPending || updateMutation.isPending
	const goBack = () => navigate(ROUTES.ADMINISTRATION.CAMPAIGNS)

	const updateDraft = (index: number, draft: CampaignQuestionDraft) =>
		setDrafts((current) =>
			sanitizeConditions(
				current.map((item, i) => (i === index ? draft : item)),
			),
		)

	const moveDraft = (index: number, direction: -1 | 1) =>
		setDrafts((current) => {
			const target = index + direction
			if (target < 0 || target >= current.length) return current
			const next = [...current]
			;[next[index], next[target]] = [next[target], next[index]]
			return sanitizeConditions(next)
		})

	const handleSave = async () => {
		if (title.trim().length < 3) {
			toast.error(t('campaigns.form.errors.title'))
			return
		}
		if (startsAt && endsAt && endsAt < startsAt) {
			toast.error(t('campaigns.form.errors.dates'))
			return
		}
		if (!isGlobal && branchIds.length === 0) {
			toast.error(t('campaigns.form.errors.branches'))
			return
		}
		const questionsError = questionsLocked ? null : validateDrafts(drafts)
		if (questionsError) {
			toast.error(t(questionsError))
			return
		}

		const data = {
			title: title.trim(),
			description: description.trim() || null,
			startsAt: startsAt || null,
			endsAt: endsAt || null,
			allowMultiple,
			isGlobal,
			branchIds: isGlobal ? [] : branchIds,
			...(questionsLocked ? {} : { questions: toQuestionPayload(drafts) }),
		}

		try {
			if (editingId) {
				await updateMutation.mutateAsync({ id: editingId, data })
				toast.success(t('campaigns.form.updateSuccess'))
			} else {
				await createMutation.mutateAsync({
					...data,
					questions: toQuestionPayload(drafts),
				})
				toast.success(t('campaigns.form.createSuccess'))
			}
			goBack()
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<AdminCardPage
			icon={<ClipboardList className="size-5" />}
			title={t(
				editingId ? 'campaigns.form.editTitle' : 'campaigns.form.createTitle',
			)}
			subtitle={t('campaigns.admin.subtitle')}
			onBack={goBack}
			footerActions={
				<>
					<Button
						type="button"
						variant="cancel"
						className="w-auto"
						onClick={goBack}
					>
						{t('common.cancel')}
					</Button>
					<Button
						type="button"
						className="w-auto bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90"
						disabled={isSaving}
						onClick={handleSave}
					>
						{isSaving && <Spinner />}
						{t('campaigns.form.save')}
					</Button>
				</>
			}
		>
			{editingId && isLoading ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('common.loading')}
				</p>
			) : editingId && (isError || !campaign) ? (
				<p className="py-16 text-center text-destructive text-sm">
					{t('campaigns.admin.loadError')}
				</p>
			) : (
				<div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
					<div className="flex flex-col gap-3">
						<Input
							placeholder={t('campaigns.form.titlePlaceholder')}
							value={title}
							maxLength={200}
							onChange={(e) => setTitle(e.target.value)}
						/>
						<Textarea
							rows={3}
							placeholder={t('campaigns.form.descriptionPlaceholder')}
							value={description}
							maxLength={2000}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					<div className="grid gap-3 sm:grid-cols-2">
						<label className="flex flex-col gap-1 text-xs font-medium text-kaefer-dark-grey">
							{t('campaigns.form.startsAt')}
							<Input
								type="date"
								value={startsAt}
								max={endsAt || undefined}
								onChange={(e) => setStartsAt(e.target.value)}
							/>
						</label>
						<label className="flex flex-col gap-1 text-xs font-medium text-kaefer-dark-grey">
							{t('campaigns.form.endsAt')}
							<Input
								type="date"
								value={endsAt}
								min={startsAt || undefined}
								onChange={(e) => setEndsAt(e.target.value)}
							/>
						</label>
					</div>

					<div className="flex flex-col gap-3 rounded-lg border border-kaefer-light-silver bg-white p-4">
						<label className="flex items-center justify-between gap-3 text-sm text-kaefer-dark-grey">
							<span>
								{t('campaigns.form.allowMultiple')}
								<span className="block text-xs text-muted-foreground">
									{t('campaigns.form.allowMultipleHint')}
								</span>
							</span>
							<Switch
								checked={allowMultiple}
								onCheckedChange={setAllowMultiple}
							/>
						</label>
						<label className="flex items-center justify-between gap-3 text-sm text-kaefer-dark-grey">
							<span>
								{t('campaigns.form.global')}
								<span className="block text-xs text-muted-foreground">
									{t('campaigns.form.globalHint')}
								</span>
							</span>
							<Switch checked={isGlobal} onCheckedChange={setIsGlobal} />
						</label>
						{!isGlobal && (
							<MultiSelect
								options={(orgData ?? []).map((branch) => ({
									value: branch.id,
									label: branch.name,
								}))}
								values={branchIds}
								onValuesChange={setBranchIds}
								placeholder={t('campaigns.form.branchesPlaceholder')}
								searchPlaceholder={t('campaigns.form.searchBranch')}
								emptyMessage={t('campaigns.form.noBranch')}
							/>
						)}
					</div>

					<div className="flex flex-col gap-3">
						<h2 className="text-sm font-semibold text-kaefer-dark-grey">
							{t('campaigns.form.questions')}
						</h2>
						{questionsLocked ? (
							<p className="rounded-md bg-kaefer-honey-yellow/20 p-3 text-sm text-kaefer-dark-grey">
								{t('campaigns.form.questionsLocked', {
									count: campaign?.responseCount ?? 0,
								})}
							</p>
						) : (
							<>
								{drafts.map((draft, index) => (
									<CampaignQuestionEditor
										key={draft.key}
										index={index}
										total={drafts.length}
										draft={draft}
										drafts={drafts}
										onChange={(next) => updateDraft(index, next)}
										onMove={(direction) => moveDraft(index, direction)}
										onRemove={() =>
											setDrafts((current) =>
												sanitizeConditions(
													current.filter((_, i) => i !== index),
												),
											)
										}
									/>
								))}
								<Button
									type="button"
									variant="outline"
									className="w-auto self-start"
									onClick={() =>
										setDrafts((current) => [...current, createQuestionDraft()])
									}
								>
									<Plus className="size-4" />
									{t('campaigns.form.addQuestion')}
								</Button>
							</>
						)}
					</div>
				</div>
			)}
		</AdminCardPage>
	)
}
